import { memo, useEffect, useRef, useState } from 'react';
import { BookOpen, Folder, HelpCircle, Home, Settings as SettingsIcon, LogOut as LogOutIcon } from 'react-feather';
import { Link, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { User } from '@trinity/chat-components/components/icons/User.js';
import { useActiveApplicationIdentifier } from '@trinity/chat-components/containers/UserApplications/useActiveApplicationIdentifier.js';
import { useModal } from '@trinity/chat-components/hooks/useModal.js';
import { getRoleName } from '@trinity/management-app-client/enums';
import { HorizontalNavLogo } from '@/components/Branding/Logo';
import { InsightsChatIcon } from '@/components/icons/InsightsChat';
import { WorkspaceGlobalSearchBar } from '@/components/WorkspaceGlobalSearchBar';
import { isLowerEnv } from '@/config/utils';
import { useAppSelector } from '@/store';
import { useHasWorkspacesEnabled } from '@/store/selectors';
import { SupportModal } from '../SupportModal/SupportModal';
import { ApplicationSelector } from './ApplicationSelector';
import { UserBrandDropdown } from './Dropdown';

export const NavBar = memo(() => {
  const workspacesEnabled = useHasWorkspacesEnabled();
  const authenticated = useAppSelector((state) => state.appState.authenticated);
  const username = useAppSelector((state) => state.user.profile.name);
  const photoUrl = useAppSelector((state) => state.user.profile.photoUrl);
  const groupFeatures = useAppSelector((state) => state.features);
  const roles = useAppSelector((state) => state.user.roles);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropDownOpen] = useState(false);

  const [toggleSupportModal, SModal] = useModal(SupportModal);

  const handleDropdownToggle = () => {
    setIsDropDownOpen((prev) => !prev);
  };

  // const homeRoute = useSelectHomePageLocation();

  useEffect(() => {
    if (!isDropdownOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropDownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);
  const location = useLocation();
  const isSegmentation = location.pathname.startsWith('/segmentation');
  const showWorkspaceNav = workspacesEnabled && !isSegmentation;
  const applicationIdentifier = useActiveApplicationIdentifier();
  const TEST_APP_ID = 'e37f9d187e1f4a89a816b471fd0bb882';
  const PROD_APP_ID = '44be68d1b85b44f5b4c01fe302da5838';
  const showMessageLibrary = isLowerEnv()
    ? applicationIdentifier === TEST_APP_ID
    : applicationIdentifier === PROD_APP_ID;

  return (
    <Root>
      <Wrap>
        <Left>
          {authenticated && !isSegmentation && <ApplicationSelector />}
          <Link to="/home">
            <HorizontalNavLogo />
          </Link>
          {showWorkspaceNav && <WorkspaceGlobalSearchBar />}
        </Left>

        {authenticated && (
          <Right>
            <SModal />
            {groupFeatures.homeButtonTopHeader && (
              <NavLink to="/home">
                <StyledHome />
                <NavLabel>Home</NavLabel>
              </NavLink>
            )}
            {groupFeatures.supportIcon && (
              <NavItem onClick={toggleSupportModal}>
                <StyledSupport />
                <NavLabel>Support</NavLabel>
              </NavItem>
            )}
            {showWorkspaceNav && groupFeatures.documentIconVisibility && (
              <NavLink to="/workspaces">
                <StyledFolder />
                <NavLabel>Documents</NavLabel>
              </NavLink>
            )}
            {showMessageLibrary && (
              <NavLink to="/message-library">
                <StyledBookOpen />
                <NavLabel>Message Library</NavLabel>
              </NavLink>
            )}
            {groupFeatures.chatIconVisibility && (
              <NavLink to="/chat">
                <StyledInsightsChatIcon />
                <NavLabel>Insights Chat</NavLabel>
              </NavLink>
            )}
            <StyledDropdown ref={dropdownRef}>
              <DropdownToggle onClick={handleDropdownToggle}>
                {photoUrl ? <UserAvatar url={photoUrl} /> : <StyledUser />}
                <NavLabel>Account</NavLabel>
              </DropdownToggle>
              <DropdownMenu open={isDropdownOpen}>
                <AccountDropdownItem>
                  {photoUrl ? <DropdownUserAvatar url={photoUrl} /> : <StyledUserIcon />}
                  <AccountDropdownText>
                    <AccountName>{username}</AccountName>
                    <AccountRole>{roles.map((r) => getRoleName(r)).join(', ')}</AccountRole>
                  </AccountDropdownText>
                </AccountDropdownItem>
                <BrandSection>
                  <BrandLabel>Brand</BrandLabel>
                  <UserBrandDropdown />
                </BrandSection>
                <DropdownItem to="/settings" onClick={() => setIsDropDownOpen(false)}>
                  <MenuItemRow>
                    <SettingsIcon size={18} />
                    Settings
                  </MenuItemRow>
                </DropdownItem>
                <DropdownItem to="/logout">
                  <MenuItemRow>
                    <LogOutIcon size={18} />
                    Log Out
                  </MenuItemRow>
                </DropdownItem>
              </DropdownMenu>
            </StyledDropdown>
          </Right>
        )}
      </Wrap>
    </Root>
  );
});

const Root = styled.div(({ theme }) => ({
  top: 0,
  width: '100%',
  position: 'fixed',
  boxShadow: '0 0 0 0 #0000001a,0 1px 0 0 #0000001a,0 0 0 0 #0000001f',
  backgroundColor: 'white',
  zIndex: 5,
}));

const Wrap = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 40px 0 27px',
  height: 60,
  width: '100%',
});

const Left = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;

  @media (min-width: 641px) {
    gap: 20px;
  }

  @media (min-width: 900px) {
    gap: 25px;
  }

  @media (min-width: 1200px) {
    gap: 45px;
  }
`;

const StyledFolder = styled(Folder)`
  color: var(--black-l);
  width: 30px;
  height: 30px;

  @media (min-width: 641px) {
    margin-bottom: 4px;
  }
`;

const StyledHome = styled(Home)`
  color: var(--black-l);
  width: 30px;
  height: 30px;

  @media (min-width: 641px) {
    margin-bottom: 4px;
  }
`;

const StyledSupport = styled(HelpCircle)`
  color: var(--black-l);
  width: 30px;
  height: 30px;

  @media (min-width: 641px) {
    margin-bottom: 4px;
  }
`;

const StyledInsightsChatIcon = styled(InsightsChatIcon)`
  color: var(--black-l);
  width: 30px;
  height: 30px;

  @media (min-width: 641px) {
    margin-bottom: 4px;
  }
`;

const StyledBookOpen = styled(BookOpen)`
  color: var(--black-l);
  width: 30px;
  height: 30px;

  @media (min-width: 641px) {
    margin-bottom: 4px;
  }
`;

const StyledUser = styled(User)`
  color: var(--black-l);
  width: 30px;
  height: 30px;

  @media (min-width: 641px) {
    margin-bottom: 4px;
  }
`;

const NavLabel = styled.div`
  display: none;

  @media (min-width: 641px) {
    display: block;
    color: var(--black-l);
    font-family: var(--font-semibold);
    font-size: 11px;
    text-align: center;
    white-space: nowrap;
  }
`;

const StyledDropdown = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NavLink = styled(Link)(({ theme }) => ({
  background: 'none',
  border: 'none',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: theme.palette.gray.dark1,
  height: '60px',
  ':hover': {
    color: theme.palette.gray.dark1,
  },
}));

const NavItem = styled.div(({ theme }) => ({
  background: 'none',
  border: 'none',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: theme.palette.gray.dark1,
  height: '60px',
}));

const DropdownToggle = styled.button(({ theme }) => ({
  background: 'none',
  border: 'none',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: theme.palette.gray.dark1,
  height: '60px',
}));

const DropdownMenu = styled.div<{ open: boolean }>`
  display: ${({ open }) => (open ? 'flex' : 'none')};
  position: absolute;
  top: 60px;
  right: 0;
  z-index: 3;
  min-width: 170px;
  background-color: var(--pri-01);
  background-clip: padding-box;
  box-shadow: 1px 1px 2px 1px rgba(238, 238, 238, 1);
  border-bottom-right-radius: 6px;
  border-bottom-left-radius: 6px;
  padding-bottom: 15px;
  flex-direction: column;
  justify-content: center
  align-items: center;
  border: ${({ theme }) => `1px solid ${theme.palette.gray.light1}`};,
`;

const DropdownItem = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  boxSizing: 'border-box',
  color: 'inherit',
  textDecoration: 'none',
  ':hover': {
    backgroundColor: 'var(--gray-xxl)',
  },
}));

const StyledUserIcon = styled(User)`
  // Replaces StyledUser
  color: var(--black-l);
  width: 35px;
  height: 35px;
  margin-left: 0px;
  font-family: ${({ theme }) => theme.fonts.regular}; /* Added */
`;

const DropdownUserAvatar = styled.div<{ url: string }>`
  width: 35px;
  height: 35px;
  background-image: ${({ url }) => `url(${url})`};
  background-size: cover;
  border-radius: 50%;
`;

const UserAvatar = styled.div<{ url: string }>`
  width: 30px;
  height: 30px;
  background-image: ${({ url }) => `url(${url})`};
  background-size: cover;
  border-radius: 50%;

  @media (min-width: 641px) {
    margin-bottom: 4px;
  }
`;

const AccountName = styled.div`
  // Replaces Name
  font-family: ${({ theme }) => theme.fonts.regular};
  font-size: 16px;
  color: black;
  margin-bottom: 8px;
  font-weight: 900;
`;

const AccountRole = styled.div`
  // Replaces Role
  font-family: ${({ theme }) => theme.fonts.regular}; /* Updated */
  font-size: 14px;
  color: var(--gray-d);
`;

const AccountDropdownText = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  fontFamily: theme.fonts.regular,
  color: 'var(--black-l)',
  alignItems: 'flex-start',
  marginLeft: '15px',
}));

const AccountDropdownItem = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '20px 20px',
  width: '250px',
}));

const BrandSection = styled.div`
  padding: 0 20px 12px;
  width: 100%;
  box-sizing: border-box;
`;

const BrandLabel = styled.div`
  font-family: ${({ theme }) => theme.fonts.semiBold};
  font-size: 12px;
  font-weight: 700;
  color: var(--gray-d);
  margin-bottom: 6px;
`;

const MenuItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 20px;
  font-family: ${({ theme }) => theme.fonts.regular};
  font-size: 15px;
  color: var(--gray-d);
  cursor: pointer;
`;
